import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';

import { AppRoutes } from '@/components/routing/AppRoutes';
import { RouteErrorBoundary } from '@/components/routing/ErrorBoundaries';
import { LanguageRedirect } from '@/components/routing/LanguageRedirect';
import { LanguageWrapper } from '@/components/routing/LanguageWrapper';
import { FullscreenGalleryProvider } from '@/contexts/FullscreenGalleryContext';
import { SUPPORTED_LANGUAGES } from '@/utils/language';
import { getCurrentPage } from '@/utils/page-detector';

import Layout from './Layout.tsx';

// Loading fallback component for lazy-loaded routes
const PageLoader: React.FC = () => (
  <div className="min-h-screen bg-surface-muted" />
);

// Conditional loader that doesn't show full screen loading for admin routes
const ConditionalPageLoader: React.FC = () => {
  const location = useLocation();

  // Check if this is an admin route (supports both /admin-dashboard and /admin paths with language prefixes)
  const isAdminRoute = /\/admin(?:-dashboard)?(?:\/|$)/.test(location.pathname);

  if (isAdminRoute) {
    return null; // Don't show full screen loading for admin routes
  }

  return <PageLoader />;
};

// Create a wrapper component that uses useLocation inside the Router context
function PagesContent(): React.ReactElement {
  const location = useLocation();
  const currentPage = getCurrentPage(location.pathname);

  return (
    <Routes>
      {/* Redirect root to language-prefixed URL */}
      <Route path="/" element={<LanguageRedirect />} />

      {/* All routes with language prefix */}
      <Route
        path="/:lang/*"
        element={
          <LanguageWrapper>
            {({ currentLanguage }) => (
              <FullscreenGalleryProvider>
                <Layout currentPageName={currentPage} language={currentLanguage}>
                  <RouteErrorBoundary>
                    <Suspense fallback={<ConditionalPageLoader />}>
                      <AppRoutes />
                    </Suspense>
                  </RouteErrorBoundary>
                </Layout>
              </FullscreenGalleryProvider>
            )}
          </LanguageWrapper>
        }
      />

      {/* Catch-all: redirect any non-localized URLs to localized version */}
      <Route path="*" element={<LanguageAgnosticRedirect />} />
    </Routes>
  );
}

// Helper component to check if path starts with supported language
const LanguageAgnosticRedirect = () => {
  const location = useLocation();

  // Check if current path already has a valid language prefix
  const pathPart = location.pathname.split('/').find(Boolean);
  const firstPart = pathPart;

  // If it already has a language prefix, redirect to root (should be handled by /:lang/* route)
  if (firstPart && SUPPORTED_LANGUAGES.includes(firstPart)) {
    return <Navigate to="/" replace />;
  }

  // Otherwise, use normal language redirect
  return <LanguageRedirect />;
};

export default function Pages(): React.JSX.Element {
  return (
    <Router>
      <PagesContent />
    </Router>
  );
}
